import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {useTheme} from '@mui/material/styles';
import {HoverLabelTextField, SnackbarField} from '@/components/common';
import * as CompanyGLCategoryService from '@/services/accountService/companyGLCategoryService';
import * as GlCategoryService from '@/services/accountService/glCategoryService';
import {
    buildAliasExportFileName,
    exportAliasMappingsToXlsx,
    parseAliasMappingsFromXlsx,
} from './coGlAliasExcel';

type LeafOption = { code: string; label: string };

const PAGE_SIZE_OPTIONS = [25, 50, 100];

/** 库里常见 Dr/Cr，下拉用 DR/CR */
function normalizeNormalSide(side?: string | null): 'DR' | 'CR' | undefined {
    const s = String(side || '').trim().toUpperCase();
    if (s === 'DR' || s === 'CR') return s;
    return undefined;
}

function GlCodeCell({
    value,
    options,
    optionByCode,
    onChange,
}: {
    value: string;
    options: LeafOption[];
    optionByCode: Map<string, LeafOption>;
    onChange: (code: string) => void;
}) {
    const [open, setOpen] = useState(!value);
    const selected = value ? optionByCode.get(value) ?? null : null;

    if (!open) {
        return (
            <Button
                size="small"
                fullWidth
                variant="text"
                onClick={() => setOpen(true)}
                sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    color: 'text.primary',
                    fontWeight: 400,
                    px: 1,
                }}
            >
                {selected?.label || value || '— Select —'}
            </Button>
        );
    }

    return (
        <Autocomplete
            size="small"
            openOnFocus
            autoHighlight
            options={options}
            value={selected}
            onChange={(_, opt) => {
                onChange(opt?.code || '');
                if (opt?.code) setOpen(false);
            }}
            onBlur={() => {
                if (value) setOpen(false);
            }}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            renderInput={(params) => (
                <TextField {...params} autoFocus placeholder="Search GL code" margin="none"/>
            )}
        />
    );
}

interface CompanyGLCategoryOperaProps {
    companyId: number;
    companyName?: string;
}

type EditableRow = CompanyGLCategoryService.GlAccount & {
    _draft?: boolean;
    _dirty?: boolean;
    _localKey: string;
};

const REASON_LABEL: Record<string, string> = {
    LEGACY_CONFIRM: '旧格式确认',
    NEW_FORMAT_SYNC: '新格式同步',
    MANUAL_CRUD: '页面操作',
};

const TYPE_LABEL: Record<string, string> = {
    UPDATE: '修改',
    DELETE: '删除',
};

/**
 * 公司 GL 类别（别名）管理：Mappings CRUD + History 审计。
 * UX：docs/company-gl-category-ux-plan.md（P0：搜索 / 只读点行编辑 / 底部统一保存）
 */
const CompanyGLCategoryOpera: React.FC<CompanyGLCategoryOperaProps> = ({companyId, companyName}) => {
    const theme = useTheme();
    const [tab, setTab] = useState<'MAPPING' | 'HISTORY'>('MAPPING');
    const [loading, setLoading] = useState(false);
    const [savingAll, setSavingAll] = useState(false);
    const [deletingKey, setDeletingKey] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{
        open: boolean; message?: string; severity?: 'success' | 'error'
    }>({open: false});

    const [rows, setRows] = useState<EditableRow[]>([]);
    const [leafCategories, setLeafCategories] = useState<GlCategoryService.GlCategory[]>([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [query, setQuery] = useState('');
    /** 当前编辑行；草稿行始终可编辑 */
    const [editingKey, setEditingKey] = useState<string | null>(null);

    const [history, setHistory] = useState<CompanyGLCategoryService.GlAccountHistory[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const importInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        (async () => {
            try {
                const cats = await GlCategoryService.fetchLeafGlCategories();
                setLeafCategories(cats || []);
            } catch {
                // ignore
            }
        })();
    }, []);

    const codeToName = useMemo(() => {
        const m = new Map<string, string>();
        for (const c of leafCategories) {
            if (c.code) m.set(c.code, c.nameEn || c.nameZh || c.code);
        }
        return m;
    }, [leafCategories]);

    const leafNameMap = useMemo(() => {
        const m = new Map<string, {nameEn?: string; nameZh?: string}>();
        for (const c of leafCategories) {
            if (c.code) m.set(c.code, {nameEn: c.nameEn, nameZh: c.nameZh});
        }
        return m;
    }, [leafCategories]);

    const leafCodeSet = useMemo(() => {
        const s = new Set<string>();
        for (const c of leafCategories) {
            if (c.code) s.add(c.code);
        }
        return s;
    }, [leafCategories]);

    const leafOptions = useMemo<LeafOption[]>(() => {
        return (leafCategories || [])
            .filter((c): c is GlCategoryService.GlCategory & { code: string } => !!c.code)
            .map((c) => ({
                code: c.code,
                label: `${c.code} — ${c.nameEn || c.nameZh || ''}`,
            }));
    }, [leafCategories]);

    const leafOptionByCode = useMemo(() => {
        const m = new Map<string, LeafOption>();
        for (const o of leafOptions) m.set(o.code, o);
        return m;
    }, [leafOptions]);

    const inferNormalSide = useCallback((code?: string): 'DR' | 'CR' | undefined => {
        if (!code) return undefined;
        const cat = leafCategories.find(c => c.code === code);
        const t = cat?.type?.toUpperCase();
        if (t === 'ASSET' || t === 'EXPENSE') return 'DR';
        if (t === 'LIABILITY' || t === 'EQUITY' || t === 'REVENUE') return 'CR';
        return undefined;
    }, [leafCategories]);

    const loadRows = useCallback(async () => {
        if (!companyId) return;
        setLoading(true);
        try {
            const list = await CompanyGLCategoryService.listAccountsByCompany(Number(companyId));
            setRows((list || []).map(r => ({
                ...r,
                normalSide: normalizeNormalSide(r.normalSide),
                _localKey: `row-${r.accountId}`,
            })));
            setPage(0);
            setEditingKey(null);
        } catch (e: any) {
            setSnackbar({open: true, severity: 'error', message: e?.message || 'Load mappings failed'});
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    const loadHistory = useCallback(async () => {
        if (!companyId) return;
        setHistoryLoading(true);
        try {
            const list = await CompanyGLCategoryService.listHistory({companyId: Number(companyId)});
            setHistory(list || []);
        } catch (e: any) {
            setSnackbar({open: true, severity: 'error', message: e?.message || 'Load history failed'});
        } finally {
            setHistoryLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        if (tab === 'MAPPING') loadRows();
        else loadHistory();
    }, [tab, loadRows, loadHistory]);

    const queryTrimmed = query.trim().toLowerCase();
    const filteredRows = useMemo(() => {
        if (!queryTrimmed) return rows;
        return rows.filter((r) => {
            const code = (r.code || '').toLowerCase();
            const aliasEn = (r.aliasEn || '').toLowerCase();
            const aliasZh = (r.aliasZh || '').toLowerCase();
            const std = (r.code ? codeToName.get(r.code) || '' : '').toLowerCase();
            return code.includes(queryTrimmed)
                || aliasEn.includes(queryTrimmed)
                || aliasZh.includes(queryTrimmed)
                || std.includes(queryTrimmed);
        });
    }, [rows, queryTrimmed, codeToName]);

    useEffect(() => {
        setPage(0);
    }, [queryTrimmed]);

    const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize) || 1);
    const safePage = Math.min(page, pageCount - 1);
    const pagedRows = useMemo(() => {
        const start = safePage * pageSize;
        return filteredRows.slice(start, start + pageSize);
    }, [filteredRows, safePage, pageSize]);

    useEffect(() => {
        if (page > pageCount - 1) setPage(Math.max(0, pageCount - 1));
    }, [page, pageCount]);

    const pendingRows = useMemo(
        () => rows.filter((r) => r._draft || r._dirty),
        [rows],
    );
    const pendingCount = pendingRows.length;

    const addDraftRow = () => {
        setQuery('');
        const key = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        setRows((prev) => {
            const next = [
                ...prev,
                {
                    _localKey: key,
                    _draft: true,
                    companyId: Number(companyId),
                    code: '',
                    aliasEn: '',
                    aliasZh: '',
                    normalSide: undefined,
                    currency: 'HKD',
                    isActive: 1,
                    isDel: 0,
                },
            ];
            setPage(Math.floor((next.length - 1) / pageSize));
            return next;
        });
        setEditingKey(key);
    };

    const updateField = (key: string, patch: Partial<EditableRow>) => {
        setRows((prev) => prev.map((r) => (r._localKey === key
            ? {...r, ...patch, _dirty: !r._draft ? true : r._dirty}
            : r)));
    };

    const validate = (r: EditableRow): string | null => {
        if (!r.code || !r.code.trim()) return 'Code is required';
        if (!r.aliasEn || !r.aliasEn.trim()) return 'Alias EN is required';
        return null;
    };

    const buildPayload = (row: EditableRow): CompanyGLCategoryService.GlAccount => ({
        accountId: row.accountId,
        companyId: Number(companyId),
        code: row.code?.trim(),
        aliasEn: row.aliasEn?.trim(),
        aliasZh: row.aliasZh?.trim() || undefined,
        normalSide: normalizeNormalSide(row.normalSide) ?? inferNormalSide(row.code?.trim()),
        currency: row.currency || 'HKD',
        isActive: row.isActive ?? 1,
        isDel: 0,
    });

    const handleDiscard = async () => {
        if (pendingCount > 0 && !window.confirm(`Discard ${pendingCount} unsaved change(s)?`)) return;
        await loadRows();
    };

    const handleSaveAll = async () => {
        if (pendingCount === 0) return;
        for (const row of pendingRows) {
            const err = validate(row);
            if (err) {
                setEditingKey(row._localKey);
                const idx = filteredRows.findIndex((r) => r._localKey === row._localKey);
                if (idx >= 0) setPage(Math.floor(idx / pageSize));
                setSnackbar({open: true, severity: 'error', message: `${err} (${row.code || row.aliasEn || 'new row'})`});
                return;
            }
        }
        setSavingAll(true);
        try {
            let created = 0;
            let updated = 0;
            for (const row of pendingRows) {
                const payload = buildPayload(row);
                if (row._draft || !row.accountId) {
                    await CompanyGLCategoryService.createAccount(payload);
                    created += 1;
                } else {
                    await CompanyGLCategoryService.updateAccount(Number(row.accountId), payload);
                    updated += 1;
                }
            }
            setSnackbar({
                open: true,
                severity: 'success',
                message: `Saved${created ? ` ${created} created` : ''}${updated ? ` ${updated} updated` : ''}`.trim(),
            });
            await loadRows();
        } catch (e: any) {
            setSnackbar({open: true, severity: 'error', message: e?.message || 'Save failed'});
        } finally {
            setSavingAll(false);
        }
    };

    const handleDeleteRow = async (row: EditableRow) => {
        if (row._draft) {
            setRows((prev) => prev.filter((r) => r._localKey !== row._localKey));
            if (editingKey === row._localKey) setEditingKey(null);
            return;
        }
        if (!row.accountId) return;
        if (!window.confirm(`Delete alias "${row.aliasEn}" → ${row.code}?`)) return;
        setDeletingKey(row._localKey);
        try {
            await CompanyGLCategoryService.deleteAccount(Number(row.accountId));
            setSnackbar({open: true, severity: 'success', message: 'Deleted'});
            await loadRows();
        } catch (e: any) {
            setSnackbar({open: true, severity: 'error', message: e?.message || 'Delete failed'});
        } finally {
            setDeletingKey(null);
        }
    };

    const handleExport = () => {
        const persisted = rows.filter((r) => !r._draft && r.code);
        if (persisted.length === 0) {
            setSnackbar({open: true, severity: 'error', message: 'No mappings to export'});
            return;
        }
        try {
            exportAliasMappingsToXlsx(
                persisted,
                leafNameMap,
                buildAliasExportFileName(companyName, companyId),
            );
            setSnackbar({
                open: true,
                severity: 'success',
                message: `Exported ${persisted.length} mapping${persisted.length === 1 ? '' : 's'}`,
            });
        } catch (e: any) {
            setSnackbar({open: true, severity: 'error', message: e?.message || 'Export failed'});
        }
    };

    const handleImportClick = () => {
        if (pendingCount > 0) {
            if (!window.confirm(`Discard ${pendingCount} unsaved change(s) and continue import?`)) return;
        }
        importInputRef.current?.click();
    };

    const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        setImporting(true);
        try {
            const {rows: parsed, skippedEmpty} = await parseAliasMappingsFromXlsx(file);
            if (parsed.length === 0) {
                setSnackbar({
                    open: true,
                    severity: 'error',
                    message: skippedEmpty
                        ? `No valid rows (skipped ${skippedEmpty} empty/incomplete)`
                        : 'No rows found in file',
                });
                return;
            }

            const items = parsed
                .filter((item) => leafCodeSet.has(item.code))
                .map((item) => ({
                    code: item.code,
                    aliasEn: item.aliasEn,
                    aliasZh: item.aliasZh,
                    normalSide: normalizeNormalSide(item.normalSide) ?? inferNormalSide(item.code),
                    currency: item.currency?.trim() || 'HKD',
                }));
            const skippedInvalidCode = parsed.length - items.length;
            const skipFile = skippedEmpty + skippedInvalidCode;

            const msg = [
                `Import ${file.name}:`,
                `${items.length} row(s)`,
                skipFile ? `${skipFile} skip` : null,
            ].filter(Boolean).join(' · ');

            if (items.length === 0) {
                setSnackbar({open: true, severity: 'error', message: `${msg} — nothing to apply`});
                return;
            }
            if (!window.confirm(`${msg}\n\nBatch upsert by code now?`)) return;

            const result = await CompanyGLCategoryService.batchUpsertByCode(Number(companyId), items);
            const created = result?.created ?? 0;
            const updated = result?.updated ?? 0;
            const skipped = (result?.skipped ?? 0) + skipFile;
            const failed = result?.failed ?? 0;
            setSnackbar({
                open: true,
                severity: failed ? 'error' : 'success',
                message: `Imported: ${created} created, ${updated} updated`
                    + (skipped ? `, ${skipped} skipped` : '')
                    + (failed ? `, ${failed} failed` : ''),
            });
            await loadRows();
        } catch (err: any) {
            setSnackbar({open: true, severity: 'error', message: err?.message || 'Import failed'});
        } finally {
            setImporting(false);
        }
    };

    const isRowEditing = (r: EditableRow) => !!r._draft || r._localKey === editingKey;

    return (
        <Box sx={{p: 2}}>
            <Stack spacing={2}>
                <Paper sx={{p: 2}}>
                    <HoverLabelTextField
                        label="Company"
                        size="small"
                        fullWidth
                        value={companyName || String(companyId)}
                        disabled
                        helperText={' '}
                    />
                </Paper>

                <Paper sx={{p: 2}}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{mb: 2}}>
                        <Tab label="Alias Mappings" value="MAPPING"/>
                        <Tab label="Change History" value="HISTORY"/>
                    </Tabs>

                    {tab === 'MAPPING' && (
                        <Box>
                            <Stack direction="row" spacing={1} mb={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Button size="small" variant="contained" color="primary"
                                        sx={{textTransform: 'none'}} onClick={addDraftRow}>
                                    + Add Mapping
                                </Button>
                                <Button size="small" variant="outlined"
                                        sx={{textTransform: 'none'}} onClick={loadRows}
                                        disabled={loading || savingAll || importing}>
                                    Refresh
                                </Button>
                                <Button size="small" variant="outlined"
                                        sx={{textTransform: 'none'}} onClick={handleExport}
                                        disabled={loading || savingAll || importing
                                            || rows.filter((r) => !r._draft).length === 0}>
                                    Export
                                </Button>
                                <Button size="small" variant="outlined"
                                        sx={{textTransform: 'none'}} onClick={handleImportClick}
                                        disabled={loading || savingAll || importing}>
                                    {importing ? 'Importing…' : 'Import'}
                                </Button>
                                <input
                                    ref={importInputRef}
                                    type="file"
                                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    hidden
                                    onChange={handleImportFile}
                                />
                                <TextField
                                    size="small"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search code, alias, standard name"
                                    sx={{minWidth: 260, flex: 1}}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>
                                    {loading
                                        ? 'Loading…'
                                        : queryTrimmed
                                            ? `${filteredRows.length} / ${rows.length} mappings`
                                            : `${rows.length} mapping${rows.length === 1 ? '' : 's'}`}
                                </Typography>
                            </Stack>
                            {loading && <CircularProgress size={20} sx={{my: 1}}/>}
                            <TableContainer sx={{maxHeight: 440}}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow sx={{background: theme.palette.action.hover}}>
                                            <TableCell sx={{fontWeight: 700, width: 280}}>GL Code</TableCell>
                                            <TableCell sx={{fontWeight: 700}}>Alias EN</TableCell>
                                            <TableCell sx={{fontWeight: 700}}>Alias ZH</TableCell>
                                            <TableCell sx={{fontWeight: 700, width: 88}} align="center">Side</TableCell>
                                            <TableCell sx={{fontWeight: 700, width: 72}} align="center">CCY</TableCell>
                                            <TableCell sx={{fontWeight: 700, width: 100}} align="center">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredRows.length === 0 && !loading && (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    {rows.length === 0 ? 'No alias mappings' : 'No matches'}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {pagedRows.map((r) => {
                                            const editing = isRowEditing(r);
                                            const pending = !!(r._draft || r._dirty);
                                            return (
                                                <TableRow
                                                    key={r._localKey}
                                                    hover
                                                    selected={editing}
                                                    onClick={() => {
                                                        if (!editing) setEditingKey(r._localKey);
                                                    }}
                                                    sx={{
                                                        cursor: editing ? 'default' : 'pointer',
                                                        bgcolor: pending
                                                            ? 'action.selected'
                                                            : undefined,
                                                    }}
                                                >
                                                    <TableCell onClick={(e) => editing && e.stopPropagation()}>
                                                        {editing ? (
                                                            <GlCodeCell
                                                                value={r.code || ''}
                                                                options={leafOptions}
                                                                optionByCode={leafOptionByCode}
                                                                onChange={(nextCode) => {
                                                                    updateField(r._localKey, {
                                                                        code: nextCode,
                                                                        normalSide: inferNormalSide(nextCode) ?? r.normalSide,
                                                                    });
                                                                }}
                                                            />
                                                        ) : (
                                                            <Typography variant="body2" noWrap title={leafOptionByCode.get(r.code || '')?.label}>
                                                                {leafOptionByCode.get(r.code || '')?.label
                                                                    || r.code
                                                                    || '—'}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell onClick={(e) => editing && e.stopPropagation()}>
                                                        {editing ? (
                                                            <TextField
                                                                size="small"
                                                                fullWidth
                                                                value={r.aliasEn || ''}
                                                                onChange={(e) => updateField(r._localKey, {aliasEn: e.target.value})}
                                                            />
                                                        ) : (
                                                            <Typography variant="body2" noWrap>{r.aliasEn || '—'}</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell onClick={(e) => editing && e.stopPropagation()}>
                                                        {editing ? (
                                                            <TextField
                                                                size="small"
                                                                fullWidth
                                                                value={r.aliasZh || ''}
                                                                onChange={(e) => updateField(r._localKey, {aliasZh: e.target.value})}
                                                            />
                                                        ) : (
                                                            <Typography variant="body2" noWrap color={r.aliasZh ? 'text.primary' : 'text.disabled'}>
                                                                {r.aliasZh || '—'}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center" onClick={(e) => editing && e.stopPropagation()}>
                                                        {editing ? (
                                                            <Select
                                                                size="small"
                                                                value={normalizeNormalSide(r.normalSide) || ''}
                                                                onChange={(e) => updateField(r._localKey, {
                                                                    normalSide: normalizeNormalSide(e.target.value) ?? undefined,
                                                                })}
                                                                displayEmpty
                                                                sx={{minWidth: 72}}
                                                            >
                                                                <MenuItem value=""><em>-</em></MenuItem>
                                                                <MenuItem value="DR">DR</MenuItem>
                                                                <MenuItem value="CR">CR</MenuItem>
                                                            </Select>
                                                        ) : (
                                                            <Typography variant="body2">
                                                                {normalizeNormalSide(r.normalSide) || '—'}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center" onClick={(e) => editing && e.stopPropagation()}>
                                                        {editing ? (
                                                            <TextField
                                                                size="small"
                                                                value={r.currency || ''}
                                                                onChange={(e) => updateField(r._localKey, {currency: e.target.value})}
                                                                sx={{width: 64}}
                                                            />
                                                        ) : (
                                                            <Typography variant="body2">{r.currency || '—'}</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                        <Stack direction="row" spacing={0.25} justifyContent="center">
                                                            {!editing && (
                                                                <Tooltip title="Edit">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => setEditingKey(r._localKey)}
                                                                    >
                                                                        <EditOutlinedIcon fontSize="small"/>
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                            <Tooltip title={r._draft ? 'Discard' : 'Delete'}>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    disabled={deletingKey === r._localKey || savingAll}
                                                                    onClick={() => handleDeleteRow(r)}
                                                                >
                                                                    <DeleteOutlineIcon fontSize="small"/>
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={filteredRows.length}
                                page={safePage}
                                onPageChange={(_, p) => setPage(p)}
                                rowsPerPage={pageSize}
                                onRowsPerPageChange={(e) => {
                                    setPageSize(parseInt(e.target.value, 10) || 25);
                                    setPage(0);
                                }}
                                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                            />

                            {pendingCount > 0 && (
                                <Paper
                                    elevation={3}
                                    sx={{
                                        mt: 1.5,
                                        px: 2,
                                        py: 1.25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        flexWrap: 'wrap',
                                        bgcolor: 'background.paper',
                                        border: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        {pendingCount} unsaved change{pendingCount === 1 ? '' : 's'}
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            sx={{textTransform: 'none'}}
                                            disabled={savingAll}
                                            onClick={() => void handleDiscard()}
                                        >
                                            Discard
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            sx={{textTransform: 'none'}}
                                            disabled={savingAll}
                                            startIcon={savingAll ? <CircularProgress size={14} color="inherit"/> : undefined}
                                            onClick={() => void handleSaveAll()}
                                        >
                                            Save changes ({pendingCount})
                                        </Button>
                                    </Stack>
                                </Paper>
                            )}
                        </Box>
                    )}

                    {tab === 'HISTORY' && (
                        <Box>
                            <Stack direction="row" spacing={1} mb={1}>
                                <Button size="small" variant="outlined"
                                        sx={{textTransform: 'none'}} onClick={loadHistory}
                                        disabled={historyLoading}>
                                    Refresh
                                </Button>
                                <Typography variant="body2" color="text.secondary" sx={{alignSelf: 'center'}}>
                                    Showing snapshots taken BEFORE each change (audit trail).
                                </Typography>
                            </Stack>
                            {historyLoading && <CircularProgress size={20} sx={{my: 1}}/>}
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{background: theme.palette.action.hover}}>
                                            <TableCell sx={{fontWeight: 700}}>Change Time</TableCell>
                                            <TableCell sx={{fontWeight: 700}} align="center">Type</TableCell>
                                            <TableCell sx={{fontWeight: 700}} align="center">Reason</TableCell>
                                            <TableCell sx={{fontWeight: 700}} align="center">Source</TableCell>
                                            <TableCell sx={{fontWeight: 700}}>Code</TableCell>
                                            <TableCell sx={{fontWeight: 700}}>Alias EN</TableCell>
                                            <TableCell sx={{fontWeight: 700}}>Alias ZH</TableCell>
                                            <TableCell sx={{fontWeight: 700}} align="center">User</TableCell>
                                            <TableCell sx={{fontWeight: 700}}>IP</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {history.length === 0 && !historyLoading && (
                                            <TableRow><TableCell colSpan={9} align="center">No history
                                                records</TableCell></TableRow>
                                        )}
                                        {history.map(h => (
                                            <TableRow key={h.historyId} hover>
                                                <TableCell>{h.changeTime}</TableCell>
                                                <TableCell align="center">{TYPE_LABEL[h.changeType] || h.changeType}</TableCell>
                                                <TableCell align="center">{h.changeReason ? (REASON_LABEL[h.changeReason] || h.changeReason) : '-'}</TableCell>
                                                <TableCell align="center">{h.operationSource || '-'}</TableCell>
                                                <TableCell>
                                                    {h.code}
                                                    {codeToName.get(h.code) ? ` (${codeToName.get(h.code)})` : ''}
                                                </TableCell>
                                                <TableCell>{h.aliasEn || '-'}</TableCell>
                                                <TableCell>{h.aliasZh || '-'}</TableCell>
                                                <TableCell align="center">{h.changeUserId ?? '-'}</TableCell>
                                                <TableCell>{h.operationIp || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Paper>
            </Stack>

            <SnackbarField open={!!snackbar.open} message={snackbar.message || ''} severity={snackbar.severity}
                           onClose={() => setSnackbar(s => ({...s, open: false}))}/>
        </Box>
    );
};

export default CompanyGLCategoryOpera;
